import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { hojeISO, formatarDinheiro } from '@/pages/gerenciar/utils';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import CampoArea from '@/components/base/CampoArea';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import type { EventoPlataforma, SentidoRepasse } from '../types';
import { formatarDataBR } from '../utils';

interface Props {
  evento: EventoPlataforma;
  aoFechar: () => void;
  aoFeito: (recado: string) => void;
}

const FORMAS: { valor: string; rotulo: string }[] = [
  { valor: 'pix', rotulo: 'Pix' },
  { valor: 'transferencia', rotulo: 'Transferência' },
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'outro', rotulo: 'Outro' },
];

export default function ModalRegistrarRepasse({ evento, aoFechar, aoFeito }: Props) {
  const saldo = Number(evento.saldo_centavos) || 0;
  const sentidoInicial: SentidoRepasse =
    evento.situacao === 'cobrar' ? 'para_seliga' : 'para_organizador';

  const [sentido, setSentido] = useState<SentidoRepasse>(sentidoInicial);
  const [valorCentavos, setValorCentavos] = useState(saldo !== 0 ? Math.abs(saldo) : 0);
  const [pagoEm, setPagoEm] = useState(hojeISO());
  const [forma, setForma] = useState('pix');
  const [destino, setDestino] = useState(evento.chave_pix?.nome ?? '');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const hoje = hojeISO();

  const textoSentido =
    sentido === 'para_organizador'
      ? 'do Se Liga para o organizador'
      : 'do organizador para o Se Liga';

  const registro = `${formatarDinheiro(valorCentavos)}`;

  async function registrar() {
    setErro(null);
    if (valorCentavos <= 0) {
      setErro('Informe um valor maior que zero.');
      return;
    }
    if (!pagoEm) {
      setErro('Informe a data do repasse.');
      return;
    }
    if (pagoEm > hoje) {
      setErro('A data não pode ser no futuro.');
      return;
    }

    setEnviando(true);
    try {
      const { data, error } = await supabase.rpc('plataforma_registrar_repasse', {
        p_evento: evento.id,
        p_valor_centavos: valorCentavos,
        p_sentido: sentido,
        p_pago_em: pagoEm,
        p_forma: forma,
        p_destino: destino.trim() || null,
        p_observacao: observacao.trim() || null,
      });
      if (error) throw error;

      const r = data as { ok?: boolean; recado?: string; erro?: string } | null;
      if (r?.ok === true) {
        aoFeito(r.recado ?? 'Repasse registrado.');
        return;
      }
      setErro(r?.erro ?? 'Não foi possível registrar o repasse.');
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal aberto titulo="Registrar repasse" aoFechar={aoFechar}>
      <p className="rounded-2xl bg-warning-50 px-4 py-3 text-sm font-medium text-warning-900">
        Isto só anota o repasse. O Pix você faz no banco.
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <p className="mb-1.5 block text-sm font-medium text-foreground-800">Sentido</p>
          <div className="overflow-hidden rounded-xl border border-secondary-200 bg-white">
            <OpcaoSelecao
              tipo="radio"
              checked={sentido === 'para_organizador'}
              onChange={() => setSentido('para_organizador')}
            >
              O Se Liga pagou o organizador
            </OpcaoSelecao>
            <OpcaoSelecao
              tipo="radio"
              checked={sentido === 'para_seliga'}
              onChange={() => setSentido('para_seliga')}
            >
              O organizador pagou o Se Liga
            </OpcaoSelecao>
          </div>
        </div>

        <CampoDinheiro
          id="repasse-valor"
          name="valor"
          label="Valor"
          centavos={valorCentavos}
          onChange={setValorCentavos}
        />

        <Campo
          rotulo="Data"
          type="date"
          max={hoje}
          value={pagoEm}
          onChange={setPagoEm}
        />

        <div>
          <p className="mb-1.5 block text-sm font-medium text-foreground-800">Forma</p>
          <div className="grid grid-cols-2 gap-2">
            {FORMAS.map((f) => (
              <button
                key={f.valor}
                type="button"
                onClick={() => setForma(f.valor)}
                className={`cursor-pointer whitespace-nowrap rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
                  forma === f.valor
                    ? 'border-primary-400 bg-primary-50 text-primary-700'
                    : 'border-secondary-200 bg-white text-foreground-800 hover:bg-background-100'
                }`}
              >
                {f.rotulo}
              </button>
            ))}
          </div>
        </div>

        <Campo
          rotulo="Para quem (opcional)"
          value={destino}
          onChange={setDestino}
          placeholder="Nome de quem recebeu"
        />

        <CampoArea
          id="repasse-observacao"
          name="observacao"
          label="Observação (opcional)"
          help="ex.: código do Pix"
          simples
          maxLength={500}
          value={observacao}
          onChange={setObservacao}
        />

        <p className="text-sm text-foreground-700">
          Registrar <strong className="font-semibold">{registro}</strong> como pago {textoSentido} em{' '}
          <strong className="font-semibold">{formatarDataBR(pagoEm) || '—'}</strong>?
        </p>

        {erro && (
          <p className="flex items-start gap-1.5 text-sm text-danger-600" role="alert">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            <span className="min-w-0">{erro}</span>
          </p>
        )}

        <Botao cheio variante="primario" carregando={enviando} onClick={registrar}>
          Registrar
        </Botao>
      </div>
    </Modal>
  );
}